// The page half of browser notifications (the worker decides — data/features/notifications.ts):
// the last on-screen check, drawing, the click, taking them down once read, the permission ask,
// and the unread count in the tab title and favicon.
import { useEffect, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import type { SidebarSnapshot } from '../../../bridge/protocol';
import type { WebNotification } from '../../../data/features/notifications';
import { t } from '../../../i18n/i18n';
import { mediaUrls } from '../../media';
import { snapshotAttention } from '../../models/sidebarModel';
import './notifications.css';

const DISMISS_KEY = 'satori.notifications.askDismissedAt';
const ASK_AGAIN_MS = 14 * 24 * 3600 * 1000;

function permission(): NotificationPermission | 'unsupported' {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/** The reader is LOOKING at the page (not merely has it open in a background tab). */
function pageInFront(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

export function useWebNotifications(o: { visibleChannelId: string | null; onOpen(n: WebNotification): void }): void {
  const latest = useRef(o);
  latest.current = o;
  // channelId → what is showing for it, so reading the conversation takes them down (the apps'
  // rule: a conversation being read has no notifications).
  const shown = useRef(new Map<string, Notification[]>());

  useEffect(
    () =>
      data.onSignal((s) => {
        if (s.kind !== 'notify' || permission() !== 'granted') return;
        const n = s.notification;
        // On screen = the tab in front AND that conversation open (a reply counts: the thread is
        // the same conversation as the channel behind it, as on Apple).
        if (pageInFront() && latest.current.visibleChannelId === n.channelId) return;
        try {
          const note = new Notification(n.title, {
            body: n.body,
            icon: mediaUrls.avatar(n.authorId, 0),
            // One entry per conversation in the OS list; a newer message replaces the older one.
            tag: n.channelId,
            renotify: true,
          } as NotificationOptions);
          note.onclick = () => {
            window.focus();
            latest.current.onOpen(n);
            note.close();
          };
          note.onclose = () => {
            const list = shown.current.get(n.channelId)?.filter((x) => x !== note) ?? [];
            if (list.length) shown.current.set(n.channelId, list);
            else shown.current.delete(n.channelId);
          };
          shown.current.set(n.channelId, [...(shown.current.get(n.channelId) ?? []), note]);
        } catch (error) {
          // Android Chrome forbids `new Notification` (service-worker notifications only).
          console.warn('[satori] notification', error);
        }
      }),
    [],
  );

  // Opening a conversation (or coming back to the tab on top of one) clears its notifications.
  useEffect(() => {
    const clear = () => {
      const id = latest.current.visibleChannelId;
      if (!id || !pageInFront()) return;
      for (const note of shown.current.get(id) ?? []) note.close();
      shown.current.delete(id);
    };
    clear();
    window.addEventListener('focus', clear);
    document.addEventListener('visibilitychange', clear);
    return () => {
      window.removeEventListener('focus', clear);
      document.removeEventListener('visibilitychange', clear);
    };
  }, [o.visibleChannelId]);
}

/** A quiet card asking for permission — browsers only allow the prompt from a click. */
export function NotificationPermissionCard() {
  const [state, setState] = useState(permission);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return Date.now() - Number(localStorage.getItem(DISMISS_KEY) ?? 0) < ASK_AGAIN_MS;
    } catch {
      return false;
    }
  });
  if (state !== 'default' || dismissed) return null;
  return (
    <div className="notify-card" role="region" aria-label={t('web.notify.askTitle')}>
      <div className="notify-card-text">
        <strong>{t('web.notify.askTitle')}</strong>
        <span>{t('web.notify.askBody')}</span>
      </div>
      <div className="notify-card-actions">
        <button
          type="button"
          className="notify-card-later"
          onClick={() => {
            try {
              localStorage.setItem(DISMISS_KEY, String(Date.now()));
            } catch {
              /* a per-viewer convenience */
            }
            setDismissed(true);
          }}
        >
          {t('web.notify.later')}
        </button>
        <button type="button" className="notify-card-allow" onClick={() => void Notification.requestPermission().then((p) => setState(p))}>
          {t('web.notify.allow')}
        </button>
      </div>
    </div>
  );
}

/** What the tab badges: mentions (a number), else "something unread" (a dot). */
export function badgeCounts(snapshot: SidebarSnapshot | null): { mentions: number; unread: boolean } {
  // The sidebar's own rule (syncParity): member mute AND muted category, silenced teams, your own
  // newest root post, provably-seen channels — so the tab can never disagree with the list.
  const a = snapshotAttention(snapshot);
  return { mentions: a.mentionCount, unread: a.hasUnread };
}

let baseIcon: HTMLImageElement | null = null;

function drawFavicon(mentions: number, unread: boolean): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"][sizes="32x32"]');
  if (!link) return;
  if (!link.dataset.base) link.dataset.base = link.href;
  if (!mentions && !unread) {
    link.href = link.dataset.base;
    return;
  }
  const paint = () => {
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const g = c.getContext('2d');
    if (!g || !baseIcon) return;
    g.drawImage(baseIcon, 0, 0, 64, 64);
    const r = mentions ? 17 : 11;
    const x = 64 - r - 1;
    const y = r + 1;
    g.beginPath();
    g.arc(x, y, r + 3, 0, Math.PI * 2);
    g.fillStyle = '#0f1216';
    g.fill();
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fillStyle = mentions ? '#e5484d' : '#4c8dff';
    g.fill();
    if (mentions) {
      g.fillStyle = '#fff';
      g.font = `bold ${mentions > 9 ? 20 : 24}px system-ui, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(mentions > 99 ? '99' : String(mentions), x, y + 1);
    }
    link.href = c.toDataURL('image/png');
  };
  if (baseIcon?.complete) paint();
  else {
    baseIcon = new Image();
    baseIcon.onload = paint;
    baseIcon.src = '/icons/favicon-64.png';
  }
}

/** "(3) Producto y diseño · Satori Chat" — the count reads in a crowded tab strip. */
export function useTabBadge(snapshot: SidebarSnapshot | null, place: string | null): void {
  const { mentions, unread } = badgeCounts(snapshot);
  useEffect(() => {
    const prefix = mentions ? `(${mentions > 99 ? '99+' : mentions}) ` : unread ? '• ' : '';
    document.title = `${prefix}${place ? `${place} · ` : ''}Satori Chat`;
    drawFavicon(mentions, unread);
  }, [mentions, unread, place]);
}
